import React, { useState } from 'react';
import { DataGridPro, GridInitialState, GridColDef, GridRenderCellParams, GridSelectionModel } from '@mui/x-data-grid-pro';
import { makeStyles } from 'tss-react/mui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import { DocumentAttachmentViewModel } from '../../../../../../../api-service/clients/auto-generated-clients';
import { ButtonMenu, IButtonMenuItem } from '../../../../../../../components/button-menu';
import { ColorPalette } from '../../../../../../../components/config/color-palette';
import { win } from '../../../../../../../utilities/window';
import { useCitizenContext } from '../../../../../citizen-context';
import { formatBytes } from '../../../../../../../utilities/format-bytes';
import { Tooltip } from '../../../../../../../components/tooltip/tooltip';
import { IconButton } from '../../../../../../../components/icon-button/icon-button';
import { TrashIcon } from '../../../../../../../components/icons/trash-icon/trash-icon';
import { FasitDataGridToolbar } from '../../../../../../../components/data-grid/fasit-data-grid-toolbar';
import { useSpuIdFromUrl } from '../../../../../hooks/use-spu-id-from-url';

export interface IAttachmentTableProps {
  attachments: DocumentAttachmentTableViewModel[];
  attachFile?: () => void;
  attachCV?: () => void;
  attachStandardAttachments?: () => void;
  attachJournalNoteAttachment?: () => void;
  downloadAttachment: (fileId: string) => void;
  downloadBffAttachment?: (fileId: string) => void;
  removeAttachments: (attachments: GridSelectionModel) => void;
  isLoading: boolean;
  showStandardAttachmentMenu?: boolean;
  onlyShowAttachFileMenu?: boolean;
  downloadFileFromDocumentArchive?: boolean;
  /** Opens a file the area stores itself instead of linking to the journal's file viewer. */
  openAttachment?: (fileId: string) => void;
  readOnly?: boolean;
}

const CustomToolbar = () => FasitDataGridToolbar({ title: 'Bilag' });

export interface DocumentAttachmentTableViewModel extends DocumentAttachmentViewModel {
  isTempFile?: boolean;
}

const useStyles = makeStyles()(() =>
  ({
    topBar: {
      display: 'flex',
      justifyContent: 'flex-end',
      height: '3rem',
    },
    delete: {
      backgroundColor: '#fbe0ea',
    },
  })
);

let attachmentsToDelete: GridSelectionModel;

export function AttachmentTable(props: IAttachmentTableProps) {
  const {
    attachments,
    attachFile,
    attachCV,
    attachJournalNoteAttachment,
    attachStandardAttachments,
    downloadAttachment,
    downloadBffAttachment,
    removeAttachments,
    isLoading,
    showStandardAttachmentMenu = false,
    onlyShowAttachFileMenu = false,
    downloadFileFromDocumentArchive = false,
    openAttachment,
    readOnly = false,
  } = props;

  const [isIndeleteMode, setIsIndeleteMode] = useState<boolean>(false);
  const { citizenId } = useCitizenContext();
  const { classes: customClasses } = useStyles();
  const spuId = useSpuIdFromUrl();
  const isServiceProviderContext = spuId !== undefined;

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'id',
      flex: 1,
      hide: true,
      type: 'string',
    },
    {
      field: 'title',
      headerName: 'Titel',
      flex: 2,
      type: 'string',
    },
    {
      field: 'fileName',
      headerName: 'Filnavn',
      flex: 1,
      type: 'string',
      renderCell: ({ row }) =>
        row &&
        (openAttachment
          ? generateDownloadLink(row.fileName || '', row.id || '', openAttachment)
          : row.owner === 'bff'
          ? generateLinkForBff(row.fileName || '', row.contentReference || '', citizenId)
          : downloadFileFromDocumentArchive
            ? generateFileViewerLinkFromDocumentArchive(row, citizenId, downloadAttachment)
            : generateFileViewerLink(row, citizenId, downloadAttachment)),
    },
    {
      field: 'fileSize',
      headerName: 'Størrelse',
      flex: 1,
      type: 'number',
      valueFormatter: ({ value }) => formatBytes(value as number),
    },
    {
      field: 'download',
      headerName: '',
      flex: 1,
      type: 'string',
      filterable: false,
      renderCell: (params: GridRenderCellParams) =>
        params.row &&
        !params.row.isTempFile &&
        (params.row.owner === 'bff' && downloadBffAttachment
          ? generateDownloadLinkForDocumentArchive('Download', params.row.contentReference, downloadBffAttachment)
          : downloadFileFromDocumentArchive
            ? generateDownloadLinkForDocumentArchive('Download', params.row.contentReference, downloadAttachment)
            : generateDownloadLink('Download', params.id as string, downloadAttachment)),
    },
  ];

  const initState: GridInitialState = {
    sorting: {
      sortModel: [{ field: 'title', sort: 'asc' }],
    },
  };

  const menuItems: IButtonMenuItem[] = [];

  if (!isServiceProviderContext) {
    menuItems.push({
      label: 'Aktuelt CV',
      onMenuClick: activateFunction(attachCV),
    });

    menuItems.push({
      label: 'Fra Journal',
      onMenuClick: activateFunction(attachJournalNoteAttachment),
    });

    if (showStandardAttachmentMenu) {
      menuItems.push({
        label: 'Fra kartotek',
        onMenuClick: activateFunction(attachStandardAttachments),
      });
    }
  }

  menuItems.push({
    label: 'Lokal fil',
    onMenuClick: activateFunction(attachFile),
  });

  const onSelectionChange = (selection: GridSelectionModel): void => {
    attachmentsToDelete = [...selection];
    selection.length > 0 ? setIsIndeleteMode(true) : setIsIndeleteMode(false);
  };

  const onDelete = (): void => {
    removeAttachments(attachmentsToDelete);
  };

  let button;

  if (isIndeleteMode) {
    button = (
      <Tooltip style={{ cursor: 'pointer' }} key="Remove" title="Fjern">
        <IconButton onClick={onDelete} size="large">
          <TrashIcon />
        </IconButton>
      </Tooltip>
    );
  } else {
    button = onlyShowAttachFileMenu ? (
      <div>
        <IconButton color="primary" sx={{ fontSize: 'small', fontWeight: 600, borderRadius: '4px', lineHeight: 'inherit' }} onClick={activateFunction(attachFile)}>
          <FontAwesomeIcon icon={faPlusCircle} style={{ marginRight: '7px' }} key="plus_icon_key" />
          Tilføj
        </IconButton>
      </div>
    ) : (
      <ButtonMenu color="primary" menuItems={menuItems} variant="text" key="attachment_text_key">
        <FontAwesomeIcon icon={faPlusCircle} style={{ marginRight: '7px' }} key="plus_icon_key" />
        Tilføj
      </ButtonMenu>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {!readOnly && <div className={`${customClasses.topBar} ${isIndeleteMode ? customClasses.delete : ''}`}>{button}</div>}
      <DataGridPro
        checkboxSelection={!readOnly}
        loading={isLoading}
        disableSelectionOnClick
        columns={columns}
        onSelectionModelChange={onSelectionChange}
        rows={attachments}
        autoHeight
        density="compact"
        initialState={initState}
        components={{
          Toolbar: CustomToolbar,
        }}
        componentsProps={{
          panel: {
            disablePortal: true,
            placement: 'bottom-end',
          },
        }}
      />
    </div>
  );
}

const isEmail = (row: DocumentAttachmentTableViewModel): boolean => row.fileName?.includes('.mht') || row.fileName?.includes('.msg') || row.fileName?.includes('.eml') || false;

function activateFunction(aFunction: (() => void) | undefined) {
  return () => {
    aFunction && aFunction();
  };
}

function generateDownloadLink(titel: string, id: string, downloadAttachment: (fileId: string) => void): JSX.Element {
  return (
    <a
      href="about:blank"
      rel="noreferrer"
      onClick={e => {
        e.preventDefault();
        downloadAttachment(id);
      }}
      style={{ color: ColorPalette.HyperLink }}
    >
      {titel}
    </a>
  );
}

const generateFileViewerLink = (row: DocumentAttachmentTableViewModel, citizenId: string, downloadAttachment: (fileId: string) => void): JSX.Element | undefined => {
  if (row && !row.isTempFile && row.fileName) {
    return isEmail(row) ? generateDownloadLink(row.fileName, row.id || '', downloadAttachment) : generateLink(row, citizenId);
  }

  return undefined;
};

function generateLink(row: DocumentAttachmentTableViewModel, citizenId: string): JSX.Element {
  const url = `${win.location.origin}/standalone/citizen/${citizenId}/journal/document/files/${row.id}`;

  return (
    <a href={url} target="_blank" rel="noreferrer" style={{ color: ColorPalette.HyperLink }}>
      {row.fileName}
    </a>
  );
}

const generateFileViewerLinkFromDocumentArchive = (
  row: DocumentAttachmentTableViewModel,
  citizenId: string,
  downloadAttachment: (fileId: string) => void
): JSX.Element | undefined => {
  if (row && !row.isTempFile && row.fileName) {
    return isEmail(row)
      ? generateDownloadLinkForDocumentArchive(row.fileName, row.contentReference || '', downloadAttachment)
      : generateLinkForDocumentArchive(row.fileName, row.contentReference || '', citizenId);
  }

  return undefined;
};

function generateLinkForDocumentArchive(fileName: string, contentReference: string, citizenId: string): JSX.Element {
  const url = `${win.location.origin}/standalone/citizen/${citizenId}/journal/note/files/documentarchive?contentReference=${contentReference}`;

  return (
    <a href={url} target="_blank" rel="noreferrer" style={{ color: ColorPalette.HyperLink }}>
      {fileName}
    </a>
  );
}

function generateLinkForBff(fileName: string, contentReference: string, citizenId: string): JSX.Element {
  const url = `${win.location.origin}/standalone/citizen/${citizenId}/journal/note/files/bff?contentReference=${contentReference}`;

  return (
    <a href={url} target="_blank" rel="noreferrer" style={{ color: ColorPalette.HyperLink }}>
      {fileName}
    </a>
  );
}

function generateDownloadLinkForDocumentArchive(titel: string, id: string, downloadAttachment: (fileId: string) => void): JSX.Element {
  return (
    <a
      href="about:blank"
      rel="noreferrer"
      onClick={e => {
        e.preventDefault();
        downloadAttachment(id);
      }}
      style={{ color: ColorPalette.HyperLink }}
    >
      {titel}
    </a>
  );
}
